const RECOVERY_STORAGE_KEY = "uniair_asset_load_recovery_v2";
const RECOVERY_QUERY_KEY = "__uniair_asset_refresh";
const RECOVERY_COOLDOWN_MS = 90 * 1000;

const CHUNK_ERROR_PATTERNS = [
  /ChunkLoadError/i,
  /Loading chunk [^ ]+ failed/i,
  /CSS_CHUNK_LOAD_FAILED/i,
  /Failed to fetch dynamically imported module/i,
  /Importing a module script failed/i,
  /error loading dynamically imported module/i,
  /Failed to load module script/i,
  /Failed to load application asset/i,
];

function errorText(error) {
  if (!error) return "";

  const parts = [
    error?.name,
    error?.message,
    error?.reason?.name,
    error?.reason?.message,
    typeof error === "string" ? error : "",
  ];

  return parts.filter(Boolean).join(" ");
}

export function isStaleAssetError(error) {
  const text = errorText(error);
  return CHUNK_ERROR_PATTERNS.some((pattern) => pattern.test(text));
}

function isBrowser() {
  return typeof window !== "undefined" && typeof document !== "undefined";
}

function currentRouteKey() {
  if (!isBrowser()) return "unknown";
  return `${window.location.pathname}${window.location.search}`;
}

function readRecoveryRecord() {
  if (!isBrowser()) return null;

  try {
    const raw = window.sessionStorage.getItem(RECOVERY_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeRecoveryRecord(source) {
  if (!isBrowser()) return;

  try {
    window.sessionStorage.setItem(
      RECOVERY_STORAGE_KEY,
      JSON.stringify({
        at: Date.now(),
        route: currentRouteKey(),
        source: source || "unknown",
      }),
    );
  } catch {
    // Some browsers can block sessionStorage. Recovery can still continue.
  }
}

function recoveryWasJustAttempted() {
  const previous = readRecoveryRecord();
  if (!previous?.at) return false;

  const age = Date.now() - Number(previous.at);
  return Number.isFinite(age) && age >= 0 && age < RECOVERY_COOLDOWN_MS;
}

async function refreshServiceWorkerRegistration() {
  if (!isBrowser() || !("serviceWorker" in navigator)) return;

  try {
    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.allSettled(
      registrations.map((registration) => registration.update()),
    );
  } catch (error) {
    console.warn("Could not refresh service worker registration:", error);
  }
}

export function forceFreshAppReload(source = "manual") {
  if (!isBrowser()) return false;

  writeRecoveryRecord(source);

  const target = new URL(window.location.href);
  target.searchParams.set(RECOVERY_QUERY_KEY, String(Date.now()));
  window.location.replace(target.toString());
  return true;
}

export async function recoverFromStaleAssetError(
  error,
  { source = "unknown" } = {},
) {
  if (!isStaleAssetError(error) || !isBrowser()) return false;

  if (recoveryWasJustAttempted()) {
    console.warn("Stale asset recovery was already attempted recently.", {
      source,
      error,
    });
    return false;
  }

  console.warn("Detected stale UniAir frontend asset. Refreshing app.", {
    source,
    error,
  });

  writeRecoveryRecord(source);

  // Firebase Messaging owns a service worker at scope '/'. It should not cache
  // application bundles, but asking the registration to update before the hard
  // navigation prevents an old worker script from lingering across deployments.
  await Promise.race([
    refreshServiceWorkerRegistration(),
    new Promise((resolve) => window.setTimeout(resolve, 400)),
  ]);

  forceFreshAppReload(source);
  return true;
}

export function clearAssetRecoveryQueryMarker() {
  if (!isBrowser()) return;

  try {
    const current = new URL(window.location.href);
    if (!current.searchParams.has(RECOVERY_QUERY_KEY)) return;

    current.searchParams.delete(RECOVERY_QUERY_KEY);
    window.history.replaceState(
      window.history.state,
      "",
      `${current.pathname}${current.search}${current.hash}`,
    );
  } catch {
    // A cosmetic URL cleanup failure should never block the app.
  }
}

function looksLikeApplicationAsset(url) {
  const value = String(url || "");
  return (
    /\/static\/(js|css)\//i.test(value) ||
    /\.chunk\.(js|css)(?:\?|$)/i.test(value)
  );
}

export function installGlobalAssetLoadRecovery() {
  if (!isBrowser()) return () => {};

  let recoveryInFlight = false;

  const attemptRecovery = (error, source) => {
    if (recoveryInFlight || !isStaleAssetError(error)) return;

    recoveryInFlight = true;
    void recoverFromStaleAssetError(error, { source }).then((reloading) => {
      // If automatic recovery was rate-limited, allow the application to keep
      // handling errors normally instead of permanently suppressing attempts.
      if (!reloading) recoveryInFlight = false;
    });
  };

  const handleWindowError = (event) => {
    const target = event?.target;
    const assetUrl = target?.src || target?.href || "";

    if (assetUrl && looksLikeApplicationAsset(assetUrl)) {
      attemptRecovery(
        new Error(`Failed to load application asset: ${assetUrl}`),
        "resource-load",
      );
      return;
    }

    if (event?.error || event?.message) {
      attemptRecovery(event.error || new Error(event.message), "window-error");
    }
  };

  const handleUnhandledRejection = (event) => {
    attemptRecovery(event?.reason, "unhandled-rejection");
  };

  // Capture is required for script/link resource errors, which do not bubble.
  window.addEventListener("error", handleWindowError, true);
  window.addEventListener("unhandledrejection", handleUnhandledRejection);

  return () => {
    window.removeEventListener("error", handleWindowError, true);
    window.removeEventListener("unhandledrejection", handleUnhandledRejection);
  };
}
